import { useEffect, useRef } from 'react';
import { useLocation } from '@react-router';
import { useHistory } from '@shared/core';
import type { SelectedPeople } from '@shared/context/people.types';
import { usePeopleContext } from '@shared/context/people';

const PERSON_IDS_PARAM = 'personIds';
const SELECTED_GUEST_KEYS_PARAM = 'selectedGuestKeys';

type SeedEntry = SelectedPeople[number];
type ToSeedEntry = (value: string) => SeedEntry;

// Each param seeds the id under the key its target picker actually reads, so the selection is valid
// from the FIRST frame — before the picker's async hydration enriches the entry. Person IDs key the
// PeoplePicker via `id`; guest keys key the ContactPicker via `personIdTag` (the send + draft paths
// read `personIdTag` for general events — `id`-only entries would send to nobody pre-hydration).
const toPersonIdEntry: ToSeedEntry = value => ({ id: value });
const toGuestKeyEntry: ToSeedEntry = value => ({ personIdTag: value });

// `location.search` includes the leading "?". Strip it before parsing — URLSearchParams drops a
// leading "?" in browsers but not in jsdom, and the repo already follows this convention (see
// updateSearchParam.ts). Keeps parse + strip correct across browser, node, and the jsdom test env.
const stripLeadingQuestionMark = (search: string): string => search.replace(/^\?/, '');

const parseIds = (search: string, param: string): string[] => {
  const raw = new URLSearchParams(stripLeadingQuestionMark(search)).get(param);
  if (!raw) return [];
  return Array.from(
    new Set(
      raw
        .split(',')
        .map(s => s.trim())
        .filter(Boolean),
    ),
  );
};

/**
 * Reads a comma-separated `?{param}=id1,id2,id3` query param from the URL and seeds PeopleContext
 * with sparse entries built by `toEntry` (each value keyed under `id` or `personIdTag` per the
 * target picker — see {@link toPersonIdEntry}/{@link toGuestKeyEntry}). The picker controllers
 * auto-hydrate these into full PersonFragment objects once their data loads.
 *
 * Strips the param from the URL after applying so a refresh doesn't re-seed. Runs exactly once per
 * mount (ref-guarded).
 *
 * Returns whether the URL carried any ids this hook will seed. Callers (e.g. RsvpReminderProvider)
 * use this to skip their own default pre-selection so it doesn't overwrite the URL-seeded recipients.
 * It reads `false` until `isReady`, which is the honest answer: nothing is seeded on those frames.
 *
 * `enabled` gates only the *seeding* — the param is always stripped from the URL regardless, so
 * unsupported flows don't leave raw ids lingering in the address bar/history for the whole compose
 * session. Seeding is gated because a sparse entry only selects a recipient in a picker whose
 * keying matches the id kind; on a mismatched picker it would inflate the selection count without
 * selecting anyone.
 *
 * `isReady` is different, and it gates *everything* — no seeding and, crucially, no stripping. Pass
 * `false` while the answer to "which id kind does this event use" is still in flight. Without it a
 * caller deriving `enabled` from event info consumed the param on the first render, when the event
 * still looked legacy, and by the time the query settled the param was gone: the guest-list handoff
 * was destroyed rather than deferred. See MGT-70.
 *
 * Must be called inside a <PeopleProvider>.
 */
const useSeedSelectedPeopleFromQueryParam = (param: string, enabled: boolean, toEntry: ToSeedEntry, isReady: boolean): boolean => {
  const location = useLocation();
  const history = useHistory();
  const { setSelectedPeople } = usePeopleContext();
  const appliedRef = useRef(false);
  // Computed during render, not from an effect, so callers see the correct value on the very FIRST
  // frame it is knowable — the seeding effect below only performs the side effects, it does not
  // drive this return value. An effect-set value would land too late: when the consumer's data query
  // is served from a warm Apollo cache, the consumer's own data-loaded effect fires in the same
  // passive-effect flush as the seeding effect and would overwrite the URL-seeded selection before
  // any re-render could propagate the flag.
  //
  // Latched on the first frame where `isReady` — the frame the caller re-rendered to tell us so, so
  // this still lands in render and not a tick later. Latching at mount instead would freeze the
  // answer from an `enabled` that was still guessing: a new-stack wedding reads `enabled: false` on
  // its first frame, and the caller would be told for the rest of the session that the URL carried
  // nothing, then have its own default pre-selection race the real one. See MGT-70.
  const hadIdsRef = useRef<boolean | undefined>(undefined);
  if (hadIdsRef.current === undefined && isReady) {
    hadIdsRef.current = enabled && parseIds(location.search, param).length > 0;
  }
  const hadIds = hadIdsRef.current ?? false;

  useEffect(() => {
    if (appliedRef.current) return;
    // Before anything else, including the strip below: a param consumed too early is unrecoverable.
    if (!isReady) return;
    const ids = parseIds(location.search, param);
    if (ids.length === 0) return;

    appliedRef.current = true;

    // Seed only on flows whose picker can resolve the seeded entries (see `enabled`).
    // The picker controller hydrates these into full PersonFragment objects.
    if (enabled) {
      setSelectedPeople(ids.map(toEntry));
    }

    // Always strip the consumed param — even when seeding is disabled — so raw ids
    // don't linger in the URL/history for the rest of the compose session.
    const params = new URLSearchParams(stripLeadingQuestionMark(location.search));
    params.delete(param);
    const nextSearch = params.toString();
    history.replace({
      ...location,
      search: nextSearch ? `?${nextSearch}` : '',
    });
  }, [enabled, history, isReady, location, param, setSelectedPeople, toEntry]);

  return hadIds;
};

/**
 * Seeds recipients from `?personIds=id1,id2`. These are person IDs, seeded under `id` because they
 * only resolve in the person-id-keyed PeoplePicker (wedding/non-general events). General-message
 * callers pass `!isGeneralEvent`: on general events the guest-key-keyed ContactPicker can't match a
 * person ID, so seeding is skipped (the param is still scrubbed).
 */
export const usePersonIdsFromQueryParams = (enabled = true, isReady = true): boolean => useSeedSelectedPeopleFromQueryParam(PERSON_IDS_PARAM, enabled, toPersonIdEntry, isReady);

/**
 * Seeds recipients from `?selectedGuestKeys=gk1,gk2`. This is how the guest-list app hands its
 * selection to the compose route. Guest keys are seeded under `personIdTag` (not `id`): that is the
 * key the general-event send + draft paths read (`resolveDraftRecipients`, the `isGeneralEvent`
 * send branch), and the ContactPicker matches a selection by `personIdTag`/`activeGuestKey` too —
 * so the selection is valid from the first frame, before the picker's async hydration enriches the
 * entry. Seeding under `id` would render correctly but send to nobody if the admin hits Send before
 * hydration (or if the contacts query errors). Mirrors the draft-rehydration seed in GeneralProvider.
 * General events use the ContactPicker, so the general-message caller passes `isGeneralEvent`. The
 * self-describing param name means no separate origin flag is needed.
 */
export const useSelectedGuestKeysFromQueryParams = (enabled = true, isReady = true): boolean =>
  useSeedSelectedPeopleFromQueryParam(SELECTED_GUEST_KEYS_PARAM, enabled, toGuestKeyEntry, isReady);
